import { useCallback, useEffect, useState } from 'react';
import { Loader2, Play, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Hint } from '@/components/Hint';
import { Can } from '@/auth/guards';
import { latestRuns, queueRun, type RunRequest, type RunSubject } from '@/api/runRequests';
import { fmtDate } from '@/features/orders/lib';
import { SessionButton } from './SessionButton';
import { POLL_MS, isOpen } from './runState';

const when = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toDateString() === new Date().toDateString()
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : fmtDate(iso);
};
const OUTCOME_LABEL: Record<string, string> = {
  ok: 'done', partial: 'partly done', failed: 'failed', skipped: 'skipped', cancelled: 'cancelled',
};

/**
 * "Run now" (the owner skill's drain, headless, on just this subject) and
 * "Work this with Claude" (an attended session in claude-manager, already
 * holding the subject). Each shows its own state: queued until the Mac picks
 * it up, running, then how the last run ended. Both are nudges server-side.
 */
export function RunButtons({ subject, size = 'sm', checkOnMount = true, runNow = true, session, onSessionChange }: {
  subject: RunSubject;
  /** The open work session the caller already read (Today's item.session); seeds the state without a read. */
  session?: RunRequest | null;
  /** Told whenever the work session's state changes (a tap, a poll). */
  onSessionChange?: (request: RunRequest | null) => void;
  size?: 'sm' | 'default';
  /** False on Today cards: only "Work this with Claude". */
  runNow?: boolean;
  /** False in long lists (Today): state is read only once a button is tapped, not per row on load. */
  checkOnMount?: boolean;
}) {
  const [runs, setRuns] = useState<{ drain: RunRequest | null; work_session: RunRequest | null } | null>(
    session ? { drain: null, work_session: session } : null,
  );
  // A fresh read by the caller (Today reloads) replaces the seeded session.
  useEffect(() => {
    if (session !== undefined) setRuns((prev) => ({ drain: prev?.drain ?? null, work_session: session }));
  }, [session]);
  const w0 = runs?.work_session ?? null;
  useEffect(() => { onSessionChange?.(w0); }, [w0, onSessionChange]);
  const [busy, setBusy] = useState<'drain' | 'work_session' | null>(null);
  const key = JSON.stringify(subject);

  const load = useCallback(async () => {
    try { setRuns(await latestRuns(JSON.parse(key) as RunSubject)); } catch { /* the buttons still work */ }
  }, [key]);
  useEffect(() => { if (checkOnMount) void load(); }, [load, checkOnMount]);

  // Poll only while something is in flight.
  const inFlight = isOpen(runs?.drain ?? null) || isOpen(runs?.work_session ?? null);
  useEffect(() => {
    if (!inFlight) return;
    const t = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(t);
  }, [inFlight, load]);

  const tap = async (kind: 'drain' | 'work_session') => {
    setBusy(kind);
    try {
      const r = await queueRun(kind, JSON.parse(key) as RunSubject);
      setRuns((prev) => ({ drain: prev?.drain ?? null, work_session: prev?.work_session ?? null, [kind]: r.request }));
      if (r.joined) toast.message(kind === 'drain' ? 'Already queued' : 'That session is already open');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not queue it');
    } finally {
      setBusy(null);
    }
  };

  const d = runs?.drain ?? null;
  const w = runs?.work_session ?? null;
  const cls = size === 'sm' ? 'h-8 gap-1.5 px-3 text-xs' : undefined;

  let drainButton: React.ReactNode;
  if (isOpen(d)) {
    const running = Boolean(d!.claimedAt);
    drainButton = (
      <Hint content={running ? 'Claude is working on it now.' : 'Starts as soon as the Mac is awake.'}>
        <span><Button variant="outline" size={size} className={cls} disabled>
          <Loader2 className="animate-spin" />{running ? 'Running…' : 'Queued'}
        </Button></span>
      </Hint>
    );
  } else {
    const hint = d && d.finishedAt
      ? `Last run ${when(d.finishedAt)}: ${OUTCOME_LABEL[d.outcome ?? ''] ?? d.outcome}${d.outcomeNote ? `. ${d.outcomeNote}` : ''}`
      : 'Claude works just this, now: its nudges, your answers and its rechecks.';
    drainButton = (
      <Hint content={hint}>
        <Button variant="outline" size={size} className={cls} disabled={busy === 'drain'} onClick={() => void tap('drain')}>
          {busy === 'drain' ? <Loader2 className="animate-spin" /> : <Play />}Run now
          {d?.outcome === 'failed' && <span className="text-danger-ink">· last failed</span>}
        </Button>
      </Hint>
    );
  }

  const workButton = (
    <SessionButton request={w} size={size}>
      <Hint content="Opens a Claude session in claude-manager that already has this item's facts, links and options.">
        <Button variant="outline" size={size} className={cls} disabled={busy === 'work_session'} onClick={() => void tap('work_session')}>
          {busy === 'work_session' ? <Loader2 className="animate-spin" /> : <Sparkles />}Work this with Claude
        </Button>
      </Hint>
    </SessionButton>
  );

  return (
    <Can code="orders:write:nudges">
      <span className="inline-flex flex-wrap items-center gap-1.5">
        {runNow && drainButton}
        {workButton}
      </span>
    </Can>
  );
}
